import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { visibleCartCount } from '@theme/custom.cart-visible-count';

/** API-only section holding just the visible count (sections/custom-cart-count.liquid). */
const COUNT_SECTION_ID = 'custom-cart-count';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {AbortController | null} */
  #historyController = null;

  /** Bumped by every cart update, so a history re-read that answers after one is dropped. */
  #cartRenderRevision = 0;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();

    // A history reload can come from the HTTP cache; pageshow may fire before this upgrades.
    if (navigationType() === 'back_forward') this.#reconcileHistoryCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
    this.#historyController?.abort();
    this.#historyController = null;
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) this.#reconcileHistoryCart();
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    this.#cartRenderRevision += 1;
    this.#historyController?.abort();

    // The bubble shows the visible count, without the nested gift-bag and protection lines that
    // `resource.item_count` includes, so the count from the returned cart section wins.
    const visibleCount = visibleCartCount(event.detail.data?.sections);
    if (visibleCount !== undefined) {
      this.renderCartBubble(visibleCount, false);
      return;
    }

    // No section HTML (e.g. the standard-events bridge): prefer the cart resource's absolute count,
    // since a relative delta drifts. Use the delta only when there is no resource (add error path).
    const resource = /** @type {{ item_count?: number } | undefined} */ (event.detail.resource);
    if (typeof resource?.item_count === 'number') {
      this.renderCartBubble(resource.item_count, false);
      return;
    }

    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };

  /**
   * Re-reads the count after a restore from history, which shows the count it was served with.
   * Not from `/cart.js`: its `item_count` includes the hidden add-on lines the bubble leaves out.
   */
  #reconcileHistoryCart = async () => {
    this.#historyController?.abort();
    const controller = new AbortController();
    const revision = this.#cartRenderRevision;
    this.#historyController = controller;

    try {
      // One fixed route: the count is the same on every page. `section_id`, not `sections`: asked
      // for JSON, /cart answers with the cart itself instead of the section map.
      const url = new URL(Theme.routes.cart_url, window.location.origin);
      url.searchParams.set('section_id', COUNT_SECTION_ID);
      const response = await fetch(url, { cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error(`Section request failed with status ${response.status}`);

      const html = await response.text();
      if (controller.signal.aborted || revision !== this.#cartRenderRevision) return;

      const count = visibleCartCount({ [COUNT_SECTION_ID]: html });
      if (count === undefined || count === this.currentCartCount) return;

      this.renderCartBubble(count, false, false);
    } catch (error) {
      if (/** @type {Error} */ (error)?.name !== 'AbortError') {
        console.warn('[cart-icon] History reconciliation failed:', error);
      }
    } finally {
      if (this.#historyController === controller) this.#historyController = null;
    }
  };
}

/**
 * How this document was loaded, from its navigation timing entry.
 * @returns {string | undefined}
 */
function navigationType() {
  const entry = globalThis.performance?.getEntriesByType?.('navigation')[0];
  if (entry?.entryType !== 'navigation') return undefined;
  return /** @type {PerformanceNavigationTiming} */ (entry).type;
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
